import { DatePicker, Form, Input } from "antd";
import { useEffect, useRef, useState } from "react";
import { HttpStatusCode } from "axios";
import { FormProvider } from "../../../../common/provider/form/Form.provider";
import { CustomButton } from "../../../../common/components/CustomButton/CustomButton";
import { SimpleInput } from "../../../../common/components/simpleInput/SimpleInput";
import { InputSelect } from "../../../../common/components/InputSelect/InputSelect";
import { DynamicUploadFile } from "../../../../common/components/Upload/DynamicUploadFile";
import { API_URLs } from "../../../../utilities/API_URLs";
import style from "../style.module.scss";

import { BookingService } from "../store/_requests";

interface BookingFormProps {
  onSuccess?: () => void;
}

export const BookingForm = ({ onSuccess }: BookingFormProps) => {
  const [form] = Form.useForm();

  const [isLoadingSubmit, setIsLoadingSubmit] =
    useState<boolean>(false);
    const [blSearchValue, setBlSearchValue] = useState<string>("");

    const [storageLoadsFound, setStorageLoadsFound] =
      useState<any[]>([]);
    
    const [isSearchingStorageLoads, setIsSearchingStorageLoads] =
      useState<boolean>(false);
    
    const [hasSearchedStorageLoads, setHasSearchedStorageLoads] =
      useState<boolean>(false);
    
    const [showAllStorageLoads, setShowAllStorageLoads] =
      useState<boolean>(false);
    
    /**
     * Permet d'ignorer une ancienne réponse API
     * si une nouvelle recherche a été lancée entre-temps.
     */
    const storageLoadSearchIdRef = useRef(0);


    useEffect(() => {
        const searchedBlNumber = blSearchValue.trim();
      
        /**
         * Si le champ est vide :
         * on remet complètement la recherche à zéro.
         */
        if (searchedBlNumber.length < 3) {
          setStorageLoadsFound([]);
          setHasSearchedStorageLoads(false);
          setIsSearchingStorageLoads(false);
          setShowAllStorageLoads(false);
      
          return;
        }
      
        /**
         * Identifiant unique de cette recherche.
         *
         * Exemple :
         * recherche 1 => MEDU
         * recherche 2 => MEDUPL
         *
         * Si MEDU répond après MEDUPL,
         * on ignore la réponse de MEDU.
         */
        const currentSearchId = ++storageLoadSearchIdRef.current;
      
        setIsSearchingStorageLoads(true);
        setHasSearchedStorageLoads(false);
        setShowAllStorageLoads(false);
      
        const timeout = setTimeout(async () => {
          try {
            const res = await BookingService.getStorageLoads(
            {
              storageLoadState:"IN",
              blNumber: searchedBlNumber
            }
            );
      
            /**
             * Une autre recherche a été lancée entre-temps.
             * On ignore donc cette réponse devenue obsolète.
             */
            if (
              currentSearchId !== storageLoadSearchIdRef.current
            ) {
              return;
            }
      
            /**
             * Selon ton API, la collection est normalement
             * disponible dans res.data.member.
             */
            const storageLoads = Array.isArray(res?.data?.member)
              ? res.data.member
              : [];
      
            setStorageLoadsFound(storageLoads);
            setHasSearchedStorageLoads(true);
          } catch (error) {
            if (
              currentSearchId !== storageLoadSearchIdRef.current
            ) {
              return;
            }
      
            console.error(
              "Erreur lors de la recherche des conteneurs :",
              error
            );
      
            setStorageLoadsFound([]);
            setHasSearchedStorageLoads(true);
          } finally {
            if (
              currentSearchId === storageLoadSearchIdRef.current
            ) {
              setIsSearchingStorageLoads(false);
            }
          }
        }, 350);
      
        return () => {
          clearTimeout(timeout);
        };
      }, [blSearchValue]);

  /**
   * Comme InputSelect utilise labelInValue,
   * la valeur peut être :
   *
   * {
   *   label: "...",
   *   value: "/api/..."
   * }
   *
   * Cette fonction permet donc de récupérer uniquement l'IRI.
   */
  const getSelectValue = (value: any) => {
    if (value === undefined || value === null) {
      return value;
    }

    if (typeof value === "object") {
      return value.value;
    }

    return value;
  };

  /**
   * Permet de récupérer BL ou DIRECT depuis :
   *
   * /api/booking_sources/BL
   * /api/booking_sources/DIRECT
   */
  const getBookingSourceId = (value: any) => {
    const iri = getSelectValue(value);

    if (!iri || typeof iri !== "string") {
      return undefined;
    }

    return iri.split("/").pop();
  };

  const onSubmitHandler = async (values: any) => {
    setIsLoadingSubmit(true);

    try {
      const bookingSource = getSelectValue(values.bookingSource);
      const bookingSourceId = getBookingSourceId(
        values.bookingSource
      );

      const customer = getSelectValue(values.customer);

      const finalCustomer = getSelectValue(values.finalCustomer);

      const invoiceNumber = getSelectValue(values.invoiceNumber);

      const deliveryNoteFile = getSelectValue(
        values.deliveryNoteFile
      );

      const rawLatestGateOutDate = getSelectValue(values.latestGateOutDate);

      /**
       * Champs communs aux deux types de booking
       */
      const dataToPost: any = {
        bookingSource,
        customer,
        finalCustomer,
        deliveryNoteFile,
      };

      /**
       *
       * format YYYY-MM-DD.
       */
      dataToPost.latestGateOutDate = rawLatestGateOutDate;

      /**
       * Facultatif :
       * on ne met invoiceNumber dans le payload
       * que s'il a réellement été renseigné.
       */
      if (invoiceNumber) {
        dataToPost.invoiceNumber = invoiceNumber;
      }

      /**
       * Booking créé depuis un BL
       */
      if (bookingSourceId === "BL") {
        dataToPost.blNumber = values.blNumber;
      }

      /**
       * Booking créé directement depuis les conteneurs
       */
      if (bookingSourceId === "DIRECT") {
        dataToPost.storageLoads =
          values.storageLoads?.map((storageLoad: any) =>
            getSelectValue(storageLoad)
          ) ?? [];
      }

      // console.log("dataToPost", dataToPost);

      const res = await BookingService.createBooking(
        dataToPost
      );

      if (
        res?.status === HttpStatusCode.Ok ||
        res?.status === HttpStatusCode.Created
      ) {
        form.resetFields();
        onSuccess?.();
      }
    } catch (error) {
      console.error(
        "Erreur lors de la création du booking :",
        error
      );
    } finally {
      setIsLoadingSubmit(false);
    }
  };

  return (
    <FormProvider
      Form={Form}
      form={form}
      debug={true}
      onFinish={(values: any) => {
        onSubmitHandler(values);
      }}
      onFinishFailed={(err) => {
        console.log("Erreur formulaire :", err);
      }}
    >
      {/* ========================= */}
      {/* PROVENANCE */}
      {/* ========================= */}

      <div>
        <SimpleInput
          Form={Form}
          label="Type de réservation"
          name="bookingSource"
          rules={[
            {
              required: true,
              message: "Veuillez sélectionner un type de réservation",
            },
          ]}
          customInput={
            <InputSelect
  uri={API_URLs.booking.bookingSourceUrl}
  onChange={() => {
    form.setFieldsValue({
      blNumber: undefined,
      storageLoads: undefined,
    });

    setBlSearchValue("");
    setStorageLoadsFound([]);
    setHasSearchedStorageLoads(false);
    setIsSearchingStorageLoads(false);
    setShowAllStorageLoads(false);

    storageLoadSearchIdRef.current += 1;
  }}
/>
          }
        />

        {/* ========================= */}
        {/* CHAMPS DYNAMIQUES */}
        {/* ========================= */}

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.bookingSource !==
            currentValues.bookingSource
          }
        >
          {({ getFieldValue }) => {
            const bookingSourceId = getBookingSourceId(
              getFieldValue("bookingSource")
            );

            if (bookingSourceId === "BL") {
  const visibleStorageLoads = showAllStorageLoads
    ? storageLoadsFound
    : storageLoadsFound.slice(0, 5);

  return (
    <div className={style.form}>
  <SimpleInput
    Form={Form}
    label="Numéro du BL"
    name="blNumber"
    rules={[
        {
          required: true,
          message: "Veuillez renseigner le numéro du BL",
        },
        {
          validator: async (_, value) => {
            if (!value) {
              return Promise.resolve();
            }
      
            /* if (isSearchingStorageLoads) {
              return Promise.reject(
                new Error("Veuillez attendre la fin de la vérification du BL")
              );
            } */
      
            if (
              (hasSearchedStorageLoads &&
              storageLoadsFound.length === 0) ||
              (value.length < 3)
            ) {
              return Promise.reject(
                new Error("Veuillez renseigner un numéro de BL valide")
              );
            }
      
            return Promise.resolve();
          },
        },
      ]}
    customInput={
      <Input
        onChange={(e) => {
          setBlSearchValue(e.target.value);
        }}
      />
    }
  />

  {/* RECHERCHE EN COURS */}
  {isSearchingStorageLoads && (
    <div className={style.form__status}>
      Conteneurs correspondants : <span>chargement...</span>
    </div>
  )}

  {/* AUCUN RÉSULTAT */}
  {!isSearchingStorageLoads &&
    hasSearchedStorageLoads &&
    storageLoadsFound.length === 0 && (
      <div className={`${style.form__status} ${style.form__status__empty}`}>
        Aucun conteneur correspondant à ce numéro de BL.
      </div>
    )}

  {/* RÉSULTATS */}
  {!isSearchingStorageLoads &&
    storageLoadsFound.length > 0 && (
      <div className={style.form__results}>
        <span className={style.form__results__label}>
          Conteneurs correspondants :
        </span>

        <div className={style.form__results__list}>
          {visibleStorageLoads.map((storageLoad: any) => (
            <span
              key={storageLoad["@id"]}
              className={style.form__results__item}
            >
              {storageLoad.uniqueIdentifierLoad}
            </span>
          ))}
        </div>

        {storageLoadsFound.length > 5 && (
          <button
            type="button"
            className={style.form__results__toggle}
            onClick={() =>
              setShowAllStorageLoads(
                (previousValue) => !previousValue
              )
            }
          >
            {showAllStorageLoads
              ? "Réduire"
              : `Afficher tous les conteneurs (${storageLoadsFound.length})`}
          </button>
        )}
      </div>
    )}
</div>
  );
}

            return null;
          }}
        </Form.Item>
      </div>

      {/* ========================= */}
      {/* CLIENT */}
      {/* ========================= */}

      <div className={`mt-3 ${style.details__grid}`}>
        <div>
        <SimpleInput
          Form={Form}
          label="Transitaire"
          name="customer"
          rules={[
            {
              required: true,
              message: "Veuillez sélectionner un transitaire",
            },
          ]}
          customInput={
            <InputSelect
              uri={
                API_URLs.settings.customers
                  .getCustomerSelect
              }
              onChange={() => {}}
            />
          }
        />
        </div>
        <div>
        <SimpleInput
          Form={Form}
          label="Client"
          name="finalCustomer"
          rules={[
            {
              required: true,
              message: "Veuillez sélectionner un client",
            },
          ]}
          customInput={
            <InputSelect
              uri={
                API_URLs.settings.customers
                  .getCustomerSelect
              }
              onChange={() => {}}
            />
          }
        />
        </div>
        </div>

        {/* ========================= */}
        {/* FACTURE */}
        {/* ========================= */}

        <div className="mt-3">
          <SimpleInput
            Form={Form}
            label="Numéro facture"
            name="invoiceNumber"
            customInput={
              <Input />
            }
          />
        </div>
      {/* ========================= */}
      {/* Date gate out au plus tard */}
      {/* ========================= */}

      <div className="mt-3">
        <SimpleInput
          Form={Form}
          label="Date gate out au plus tard"
          name="latestGateOutDate"
          rules={[
            {
              required: true,
              message: "Veuillez sélectionner un client",
            },
          ]}
          customInput={
            <DatePicker
              className="w-100"
              format="DD/MM/YYYY"
              placeholder="JJ/MM/AAAA"
            />
          }
        />
      </div>

      {/* ========================= */}
      {/* BAD */}
      {/* ========================= */}

      <div>
        <div className="mt-3">
          <SimpleInput
            Form={Form}
            label="BAD"
            name="deliveryNoteFile"
            rules={[
              {
                required: true,
                message:
                  "Veuillez joindre le BAD",
              },
            ]}
            customInput={
              <DynamicUploadFile
                form={form}
                name="deliveryNoteFile"
              />
            }
          />
        </div>

        {/* ========================= */}
        {/* CONTENEURS - DIRECT */}
        {/* ========================= */}

        <Form.Item
          noStyle
          shouldUpdate={(prevValues, currentValues) =>
            prevValues.bookingSource !==
            currentValues.bookingSource
          }
        >
          {({ getFieldValue }) => {
            const bookingSourceId = getBookingSourceId(
              getFieldValue("bookingSource")
            );

            if (bookingSourceId === "DIRECT") {
              return (
                <div className="mt-3">
                  <SimpleInput
                    Form={Form}
                    label="Conteneurs à réserver"
                    name="storageLoads"
                    rules={[
                      {
                        required: true,
                        message:
                          "Veuillez sélectionner au moins un conteneur",
                      },
                    ]}
                    customInput={
                      <InputSelect
                        mode="multiple"
                        uri={`${API_URLs.transports.selectStorage}?storageLoadState=IN&operationLoad.loadType=CONTAINER`}
                        onChange={() => {}}
                        valueOption={{
                          labelKey:
                            "uniqueIdentifierLoad",
                          valueKey: "@id",
                        }}
                      />
                    }
                  />
                </div>
              );
            }

            return null;
          }}
        </Form.Item>
      </div>

      {/* ========================= */}
      {/* SUBMIT */}
      {/* ========================= */}

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          width: "100%",
          marginTop: "1rem",
        }}
      >
        <CustomButton
          type="submit"
          isLoading={isLoadingSubmit}
        >
          Envoyer
        </CustomButton>
      </div>
    </FormProvider>
  );
};